import React, { useEffect, useState, useRef, useCallback, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import EditorToolbar from './documents/EditorToolbar';
import _ from 'lodash';
import * as pdfjsLib from 'pdfjs-dist';

interface UnifiedDocumentEditorProps {
  content: string;
  fileType: 'docx' | 'text' | 'pdf';
  onContentChange: (content: string) => void;
  onSave?: () => void;
  isFullscreen?: boolean;
  pdfUrl?: string;
}

const UnifiedDocumentEditor = React.forwardRef<HTMLDivElement, UnifiedDocumentEditorProps>(
  ({ content, fileType, onContentChange, onSave, isFullscreen = false, pdfUrl }, ref) => {
    const { t } = useTranslation();
    const editorRef = useRef<HTMLDivElement | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [pdfPages, setPdfPages] = useState<number>(0);
    const [isPdfLoading, setIsPdfLoading] = useState(false);
    const [history, setHistory] = useState<{ content: string; selection: Range | null }[]>([{ content, selection: null }]);
    const [historyIndex, setHistoryIndex] = useState<number>(0);
    const [currentBlockType, setCurrentBlockType] = useState<string>('Normal text');
    const [isBold, setIsBold] = useState(false);
    const [isItalic, setIsItalic] = useState(false);
    const [isUnderline, setIsUnderline] = useState(false);
    const [isUnorderedList, setIsUnorderedList] = useState(false);
    const [isOrderedList, setIsOrderedList] = useState(false);

    // Forward the internal ref to the parent component
    useImperativeHandle(ref, () => editorRef.current as HTMLDivElement);

    // Initialize editor content
    useEffect(() => {
      // Skip for DOCX files - they render in iframe
      if (fileType === 'docx') {
        return;
      }

      if (editorRef.current && content) {

        editorRef.current.innerHTML = content;
        editorRef.current.focus();

        // Log what the editor actually rendered
      }
    }, [content, fileType]);

    // Save selection utility
    const saveSelection = useCallback((): Range | null => {
      const selection = window.getSelection();
      return selection && selection.rangeCount > 0 ? selection.getRangeAt(0).cloneRange() : null;
    }, []);

    // Restore selection utility
    const restoreSelection = useCallback((range: Range | null) => {
      if (range) {
        const selection = window.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);
      }
    }, []);

    // Get block ancestor helper
    const getBlockAncestor = useCallback((node: Node): HTMLElement | null => {
      let current: Node | null = node;
      while (current && current !== editorRef.current) {
        if (current.nodeType === 1) {
          const tag = (current as HTMLElement).tagName.toLowerCase();
          if (['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(tag)) return current as HTMLElement;
        }
        current = current.parentNode;
      }
      return null;
    }, []);

    // Update formatting states
    const updateFormattingStates = useCallback(() => {
      setIsBold(document.queryCommandState('bold'));
      setIsItalic(document.queryCommandState('italic'));
      setIsUnderline(document.queryCommandState('underline'));
      setIsUnorderedList(document.queryCommandState('insertUnorderedList'));
      setIsOrderedList(document.queryCommandState('insertOrderedList'));
    }, []);

    // Update block type
    const updateBlockType = useCallback(
      _.debounce(() => {
        if (!editorRef.current) return;
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return;
        const anchorNode = selection.anchorNode;
        if (!anchorNode) return;
        const blockElement = getBlockAncestor(anchorNode);
        if (blockElement) {
          const tag = blockElement.tagName.toLowerCase();
          setCurrentBlockType(tag.startsWith('h') && tag.length === 2 ? `Header ${tag[1]}` : 'Normal text');
        } else {
          setCurrentBlockType('Normal text');
        }
      }, 100),
      [getBlockAncestor]
    );

    // Update history with debounce
    const debouncedUpdateHistory = useCallback(
      _.debounce(() => {
        if (!editorRef.current) return;
        const currentContent = editorRef.current.innerHTML;
        const selection = saveSelection();
        if (currentContent !== history[historyIndex]?.content) {
          const newHistory = history.slice(0, historyIndex + 1);
          newHistory.push({ content: currentContent, selection });
          setHistory(newHistory);
          setHistoryIndex(newHistory.length - 1);
        }
      }, 300),
      [history, historyIndex, saveSelection]
    );

    // Handle input
    const handleInput = useCallback(
      (e: React.FormEvent<HTMLDivElement>) => {
        const newContent = (e.target as HTMLDivElement).innerHTML;
        onContentChange(newContent);
        debouncedUpdateHistory();
      },
      [onContentChange, debouncedUpdateHistory]
    );

    // Undo/Redo
    const undo = useCallback(() => {
      if (historyIndex > 0) {
        const newIndex = historyIndex - 1;
        setHistoryIndex(newIndex);
        if (editorRef.current) {
          editorRef.current.innerHTML = history[newIndex].content;
          restoreSelection(history[newIndex].selection);
          onContentChange(history[newIndex].content);
        }
      }
    }, [historyIndex, history, restoreSelection, onContentChange]);

    const redo = useCallback(() => {
      if (historyIndex < history.length - 1) {
        const newIndex = historyIndex + 1;
        setHistoryIndex(newIndex);
        if (editorRef.current) {
          editorRef.current.innerHTML = history[newIndex].content;
          restoreSelection(history[newIndex].selection);
          onContentChange(history[newIndex].content);
        }
      }
    }, [historyIndex, history, restoreSelection, onContentChange]);

    // Keyboard shortcuts
    useEffect(() => {
      const handleKeyDown = (e: KeyboardEvent) => {
        const isMac = navigator.platform.toUpperCase().includes('MAC');
        const modKey = isMac ? e.metaKey : e.ctrlKey;

        // Handle Cmd+S / Ctrl+S for saving at document level
        if (modKey && e.key.toLowerCase() === 's') {
          e.preventDefault();
          if (onSave) onSave();
          return;
        }

        // Only apply other shortcuts if editor is focused
        if (editorRef.current && editorRef.current.contains(document.activeElement)) {
          if (modKey) {
            switch (e.key.toLowerCase()) {
              case 'b':
                e.preventDefault();
                document.execCommand('bold', false, undefined);
                updateFormattingStates();
                break;
              case 'i':
                e.preventDefault();
                document.execCommand('italic', false, undefined);
                updateFormattingStates();
                break;
              case 'u':
                e.preventDefault();
                document.execCommand('underline', false, undefined);
                updateFormattingStates();
                break;
              case 'z':
                e.preventDefault();
                undo();
                break;
              case 'y':
                e.preventDefault();
                redo();
                break;
              default:
                break;
            }
          }
        }
      };

      const editor = editorRef.current;
      if (editor) {
        editor.addEventListener('keyup', updateFormattingStates);
        editor.addEventListener('click', updateFormattingStates);
        document.addEventListener('selectionchange', updateFormattingStates);
      }

      // Attach keyboard listener to document for global Cmd+S/Ctrl+S support
      document.addEventListener('keydown', handleKeyDown);

      return () => {
        if (editor) {
          editor.removeEventListener('keyup', updateFormattingStates);
          editor.removeEventListener('click', updateFormattingStates);
          document.removeEventListener('selectionchange', updateFormattingStates);
        }
        document.removeEventListener('keydown', handleKeyDown);
      };
    }, [undo, redo, onSave, updateFormattingStates]);

    // Block type updates
    useEffect(() => {
      const editor = editorRef.current;
      if (editor) {
        editor.addEventListener('click', updateBlockType);
        editor.addEventListener('keyup', updateBlockType);
        document.addEventListener('selectionchange', updateBlockType);
      }
      return () => {
        if (editor) {
          editor.removeEventListener('click', updateBlockType);
          editor.removeEventListener('keyup', updateBlockType);
          document.removeEventListener('selectionchange', updateBlockType);
        }
      };
    }, [updateBlockType]);

    // Load PDF for rendering
    useEffect(() => {
      if (fileType === 'pdf' && pdfUrl) {
        const loadPdf = async () => {
          try {
            setIsPdfLoading(true);
            // Set worker path for pdf.js
            pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

            const pdf = await pdfjsLib.getDocument(pdfUrl).promise;
            setPdfPages(pdf.numPages);
          } catch (error) {
            console.error('Error loading PDF:', error);
            setPdfPages(0);
          } finally {
            setIsPdfLoading(false);
          }
        };

        loadPdf();
      }
    }, [fileType, pdfUrl]);

    return (
      <div className="flex flex-col h-full w-full bg-white overflow-hidden">

        {/* Show toolbar only for text files (not for read-only DOCX/PDF) */}
        {fileType === 'text' && (
          <EditorToolbar
            editorRef={editorRef}
            isBold={isBold}
            isItalic={isItalic}
            isUnderline={isUnderline}
            isUnorderedList={isUnorderedList}
            isOrderedList={isOrderedList}
            currentBlockType={currentBlockType}
            onFormatChange={() => {
              updateFormattingStates();
              updateBlockType();
              debouncedUpdateHistory();
            }}
          />
        )}

        {/* Document container - minimal styling, let DOCX formatting control everything */}
        <div
          ref={containerRef}
          style={{
            flex: 1,
            width: '100%',
            overflow: fileType === 'pdf' ? 'hidden' : 'auto',
            minHeight: 0,
            backgroundColor: '#fff',
            display: 'flex',
            justifyContent: 'center',
            alignItems: fileType === 'docx' ? 'flex-start' : 'stretch'
          }}
        >
          {fileType === 'pdf' && pdfUrl ? (
            // PDF rendering - display as embedded iframe
            <div style={{
              width: '100%',
              height: '100%',
              overflow: 'hidden'
            }}>
              {isPdfLoading && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%'
                }}>
                  <div style={{
                    animation: 'spin 1s linear infinite',
                    width: '2rem',
                    height: '2rem',
                    border: '4px solid #e5e7eb',
                    borderTop: '4px solid #3b82f6',
                    borderRadius: '50%'
                  }} />
                </div>
              )}
              <iframe
                src={`${pdfUrl}#toolbar=0&navpanes=0&scrollbar=0`}
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none'
                }}
                title="PDF Viewer"
              />
            </div>
          ) : fileType === 'docx' ? (
            // DOCX files rendered in sandboxed iframe
            // Parent scrollbar handles scrolling, iframe scroll is disabled
            <div style={{
              maxWidth: '860px',
              width: '100%',
              overflowX: 'hidden',
              backgroundColor: '#ffffff',
            }}>
              <iframe
                title="DOCX Document"
                srcDoc={content}
                style={{
                  width: '100%',
                  height: isFullscreen ? '100vh' : '90vh',
                  border: 'none',
                  margin: 0,
                  padding: 0,
                  display: 'block',
                  overflow: 'auto'
                }}
              />
            </div>
          ) : (
            // HTML/Text editor - display EXACTLY as the document defines it
            <div
              ref={editorRef}
              contentEditable={fileType !== 'pdf'}
              suppressContentEditableWarning
              style={{
                width: '100%',
                minHeight: '100%',
                padding: '2rem',
                outline: 'none',
                whiteSpace: 'pre-wrap',
                wordWrap: 'break-word'
              }}
              onInput={handleInput}
            />
          )}
        </div>
      </div>
    );
  }
);

UnifiedDocumentEditor.displayName = 'UnifiedDocumentEditor';

export default UnifiedDocumentEditor;
